import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { 
  AssetStatus, 
  getProjectAssetStatuses, 
  createProjectAssetStatus, 
  updateProjectAssetStatus,
  deleteProjectAssetStatus
} from '../../api/assets';

interface StatusManagerProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onStatusesChanged: () => void;
}

interface EditingStatus {
  id: string | null;
  name: string;
  description: string;
  status_order: number;
  requires_responsible_person: boolean;
  requires_installation: boolean;
  requires_documentation: boolean;
  estimated_duration_days: number; // Always required, no null
  max_duration_days: number; // Always required, no null
}

export const StatusManager: React.FC<StatusManagerProps> = ({ 
  projectId, 
  isOpen, 
  onClose, 
  onStatusesChanged 
}) => {
  const { keycloak } = useKeycloak();
  const [statuses, setStatuses] = useState<AssetStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingStatus, setEditingStatus] = useState<EditingStatus | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const authToken = keycloak.token;

  const fetchStatuses = async () => {
    if (!authToken) return;

    setLoading(true);
    try {
      const response = await getProjectAssetStatuses(authToken, projectId, keycloak);
      setStatuses(response.results.sort((a, b) => a.status_order - b.status_order));
    } catch (error) {
      console.error('Error fetching statuses:', error);
      toast.error('Failed to load statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
    }
  }, [isOpen, projectId, authToken]);

  const handleSave = async () => {
    if (!editingStatus || !authToken) return;

    // Validate required fields
    if (!editingStatus.name.trim()) {
      toast.error('Status name is required');
      return;
    }

    if (editingStatus.estimated_duration_days <= 0) {
      toast.error('Estimated duration must be positive');
      return;
    }

    if (editingStatus.max_duration_days <= 0) {
      toast.error('Max duration must be positive');
      return;
    }

    if (editingStatus.estimated_duration_days > editingStatus.max_duration_days) {
      toast.error('Estimated duration cannot be greater than max duration');
      return;
    }

    const statusData = {
      project_id: projectId,
      name: editingStatus.name.trim(),
      description: editingStatus.description || undefined,
      status_order: editingStatus.status_order,
      requires_responsible_person: editingStatus.requires_responsible_person,
      requires_installation: editingStatus.requires_installation,
      requires_documentation: editingStatus.requires_documentation,
      estimated_duration_days: editingStatus.estimated_duration_days,
      max_duration_days: editingStatus.max_duration_days,
    };

    console.log('💾 Saving status with data:', statusData);

    try {
      if (editingStatus.id) {
        // Update existing status
        await updateProjectAssetStatus(authToken, projectId, editingStatus.id, {
          ...statusData,
          id: editingStatus.id
        });
        toast.success('Status updated successfully');
      } else {
        // Create new status
        await createProjectAssetStatus(authToken, projectId, statusData);
        toast.success('Status created successfully');
      }
      
      await fetchStatuses();
      setEditingStatus(null);
      setShowAddForm(false);
      onStatusesChanged();
    } catch (error) {
      console.error('Error saving status:', error);
      toast.error('Failed to save status');
    }
  };

  const handleDelete = async (statusId: string) => {
    if (!authToken) return;

    if (window.confirm('Are you sure you want to delete this status?')) {
      try {
        await deleteProjectAssetStatus(authToken, projectId, statusId);
        toast.success('Status deleted successfully');
        await fetchStatuses();
        onStatusesChanged();
      } catch (error) {
        console.error('Error deleting status:', error);
        toast.error('Failed to delete status');
      }
    }
  };

  const handleEdit = (status: AssetStatus) => {
    setEditingStatus({
      id: status.id,
      name: status.name,
      description: status.description || '',
      status_order: status.status_order,
      requires_responsible_person: status.requires_responsible_person,
      requires_installation: status.requires_installation,
      requires_documentation: status.requires_documentation,
      estimated_duration_days: status.estimated_duration_days ?? 1, // Default if null
      max_duration_days: status.max_duration_days ?? 7, // Default if null
    });
    setShowAddForm(false);
  };

  const handleAddNew = () => {
    const nextOrder = Math.max(...statuses.map(s => s.status_order), 0) + 1;
    setEditingStatus({
      id: null,
      name: '',
      description: '',
      status_order: nextOrder,
      requires_responsible_person: false,
      requires_installation: false,
      requires_documentation: false,
      estimated_duration_days: 1, // Default to 1 day
      max_duration_days: 7, // Default to 7 days
    });
    setShowAddForm(true);
  };

  const handleMoveOrder = async (statusId: string, direction: 'up' | 'down') => {
    if (!authToken) return;

    const status = statuses.find(s => s.id === statusId);
    if (!status) return;

    const currentIndex = statuses.findIndex(s => s.id === statusId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= statuses.length) return;

    const targetStatus = statuses[targetIndex];
    
    try {
      // Swap the status_order values
      await updateProjectAssetStatus(authToken, projectId, status.id, {
        project_id: projectId,
        name: status.name,
        description: status.description || undefined,
        status_order: targetStatus.status_order,
        requires_responsible_person: status.requires_responsible_person,
        requires_installation: status.requires_installation,
        requires_documentation: status.requires_documentation,
        estimated_duration_days: status.estimated_duration_days ?? 1, // Fallback to 1 if null
        max_duration_days: status.max_duration_days ?? 7, // Fallback to 7 if null
        id: status.id
      });

      await updateProjectAssetStatus(authToken, projectId, targetStatus.id, {
        project_id: projectId,
        name: targetStatus.name,
        description: targetStatus.description || undefined,
        status_order: status.status_order,
        requires_responsible_person: targetStatus.requires_responsible_person,
        requires_installation: targetStatus.requires_installation,
        requires_documentation: targetStatus.requires_documentation,
        estimated_duration_days: targetStatus.estimated_duration_days ?? 1, // Fallback to 1 if null
        max_duration_days: targetStatus.max_duration_days ?? 7, // Fallback to 7 if null
        id: targetStatus.id
      });

      await fetchStatuses();
      onStatusesChanged();
    } catch (error) {
      console.error('Error reordering status:', error);
      toast.error('Failed to reorder status');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            Asset Status Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-700">Current Statuses</h3>
            <button
              onClick={handleAddNew}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
            >
              <FaPlus className="text-sm" />
              Add Status
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading statuses...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {statuses.map((status, index) => (
                <div key={status.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold text-gray-800">{status.name}</h4>
                        <span className="text-sm text-gray-500">Order: {status.status_order}</span>
                      </div>
                      {status.description && (
                        <p className="text-gray-600 mt-1">{status.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {status.requires_responsible_person && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            Requires Person
                          </span>
                        )}
                        {status.requires_installation && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                            Requires Installation
                          </span>
                        )}
                        {status.requires_documentation && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                            Requires Documentation
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleMoveOrder(status.id, 'up')}
                        disabled={index === 0}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        onClick={() => handleMoveOrder(status.id, 'down')}
                        disabled={index === statuses.length - 1}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        onClick={() => handleEdit(status)}
                        className="p-2 text-blue-500 hover:text-blue-700"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(status.id)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(editingStatus || showAddForm) && (
            <div className="mt-6 border rounded-lg p-4 bg-blue-50">
              <h4 className="font-semibold text-gray-800 mb-2">
                {editingStatus?.id ? 'Edit Status' : 'Add New Status'}
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Configure the status settings. Duration fields help track asset lifecycle timing.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={editingStatus?.name || ''}
                    onChange={(e) => setEditingStatus(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status Order *
                  </label>
                  <input
                    type="number"
                    value={editingStatus?.status_order || 0}
                    onChange={(e) => setEditingStatus(prev => prev ? { ...prev, status_order: parseInt(e.target.value) } : null)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editingStatus?.description || ''}
                    onChange={(e) => setEditingStatus(prev => prev ? { ...prev, description: e.target.value } : null)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Duration (days) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStatus?.estimated_duration_days || ''}
                    onChange={(e) => setEditingStatus(prev => prev ? { ...prev, estimated_duration_days: Math.max(0, parseInt(e.target.value) || 0) } : null)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 1"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Expected time to complete this status</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Duration (days) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingStatus?.max_duration_days || ''}
                    onChange={(e) => setEditingStatus(prev => prev ? { ...prev, max_duration_days: Math.max(0, parseInt(e.target.value) || 0) } : null)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 7"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum time allowed in this status</p>
                </div>
                <div className="md:col-span-2">
                  <h5 className="font-medium text-gray-700 mb-2">Requirements</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingStatus?.requires_responsible_person || false}
                        onChange={(e) => setEditingStatus(prev => prev ? { ...prev, requires_responsible_person: e.target.checked } : null)}
                        className="mr-2"
                      />
                      Requires Responsible Person
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingStatus?.requires_installation || false}
                        onChange={(e) => setEditingStatus(prev => prev ? { ...prev, requires_installation: e.target.checked } : null)}
                        className="mr-2"
                      />
                      Requires Installation
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingStatus?.requires_documentation || false}
                        onChange={(e) => setEditingStatus(prev => prev ? { ...prev, requires_documentation: e.target.checked } : null)}
                        className="mr-2"
                      />
                      Requires Documentation
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setEditingStatus(null);
                    setShowAddForm(false);
                  }}
                  className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={
                    !editingStatus?.name?.trim() || 
                    editingStatus.estimated_duration_days <= 0 ||
                    editingStatus.max_duration_days <= 0 ||
                    editingStatus.estimated_duration_days > editingStatus.max_duration_days
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FaSave />
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 